import { Dispatch } from 'react';

import { SurveySubjectTypes } from '~src/interfaces/surveys.interfaces';
import { defaultBaseApi, defaultPulseCheckApi, defaultReportingApi } from '~src/services/ApiController';
import { defaultQueryParams } from '~src/modules/heatmap/components/header/AttributeSelector';

import {
  DataAction, DataActionGetSurveydataError,
  DataActionGetSurveydataRequest,
  DataActionGetSurveydataResponse,
  DataActionTypes
} from './data.reducer';
import { AnalysisSpiderParams } from './types';

enum NavigationActions {
  deselectTopic = 'deselectTopic',
  selectSurveyType = 'selectSurveyType',
  selectSurvey = 'selectSurvey',
  selectCycles = 'selectCycle',
  selectTopic = 'selectTopic',
  selectEdge = 'selectEdge',
  selectAboutSegments = 'selectAboutSegments',
  setIncludeSelfAssessment = 'setIncludeSelfAssessment',
  selectAskSegments = 'selectAskSegments',
  setActiveStepIndex = 'setActiveStepIndex',
}

interface DeselectTopicAction {
  type: NavigationActions.deselectTopic;
}

interface SelectSurveyAction {
  type: NavigationActions.selectSurvey;
  id: string;
  subjectType: SurveySubjectTypes;
}
interface SelectSurveyTypeAction {
  type: NavigationActions.selectSurveyType;
  surveyType: SurveySubjectTypes;
}
interface SelectCyclesAction {
  type: NavigationActions.selectCycles;
  ids: string[];
}
interface SelectTopicAction {
  type: NavigationActions.selectTopic;
  id: string;
}
interface SelectEdgeAction {
  type: NavigationActions.selectEdge;
  position: number;
}
interface SelectAboutSegmentAction {
  type: NavigationActions.selectAboutSegments;
  ids: string[];
}
interface SetIncludeSelfAssessmentAction {
  type: NavigationActions.setIncludeSelfAssessment;
  isIncluded: boolean;
}
interface SelectAskSegmentAction {
  type: NavigationActions.selectAskSegments;
  ids: string[];
}
interface SetActiveStepIndexAction {
  type: NavigationActions.setActiveStepIndex;
  index: number;
}

export type NavigationAction =
  | DeselectTopicAction
  | SelectSurveyAction
  | SelectSurveyTypeAction
  | SelectCyclesAction
  | SelectTopicAction
  | SelectEdgeAction
  | SelectAboutSegmentAction
  | SetIncludeSelfAssessmentAction
  | SelectAskSegmentAction
  | SetActiveStepIndexAction;

export function navigationReducer(
  state: AnalysisSpiderParams = { activeStepIndex: 0 },
  action: NavigationAction,
): AnalysisSpiderParams {
  switch (action.type) {
    case NavigationActions.deselectTopic:
      return {
        ...state,
        topic: '',
        question: '',
      };
    case NavigationActions.selectSurvey:
      return {
        ...state,
        survey: action.id,
        surveySubjectType: action.subjectType,
        cycles: [],
        aboutSegments: [],
        topic: '',
        question: '',
      };
    case NavigationActions.selectSurveyType:
      return {
        ...state,
        surveySubjectType: action.surveyType,
        survey: '',
        cycles: [],
        aboutSegments: [],
      };
    case NavigationActions.selectCycles:
      return {
        ...state,
        cycles: action.ids,
      };
    case NavigationActions.selectTopic:
      return {
        ...state,
        topic: action.id,
        question: '',
      };
    case NavigationActions.selectAboutSegments:
      return {
        ...state,
        aboutSegments: action.ids,
      };
    case NavigationActions.setIncludeSelfAssessment:
      return {
        ...state,
        isSelfAssessmentIncluded: action.isIncluded,
      };
    case NavigationActions.selectAskSegments:
      return {
        ...state,
        askSegments: action.ids,
      };
    case NavigationActions.setActiveStepIndex:
      return {
        ...state,
        activeStepIndex: action.index,
      };
  }

  return state;
}

export function getAttributesAndScales() {
  return (dispatch: Dispatch<DataAction>) => {
    dispatch({ type: DataActionTypes.get_attributes_request });
    defaultBaseApi
      .getAttributesPaginated({ ...defaultQueryParams, per_page: 10000 })
      .then(res => dispatch({ type: DataActionTypes.get_attributes_response, data: res.data.items }))
      .catch(() => dispatch({ type: DataActionTypes.get_attributes_error }));

    dispatch({ type: DataActionTypes.get_scales_request });
    defaultPulseCheckApi
      .getScales({ per_page: 1000 })
      .then(res => dispatch({ type: DataActionTypes.get_scales_response, data: res.data.items }))
      .catch(() => dispatch({ type: DataActionTypes.get_scales_error }));
  };
}
export function selectSurveyTypeAction(surveyType: SurveySubjectTypes): NavigationAction {
  return {
    type: NavigationActions.selectSurveyType,
    surveyType,
  };
}
export function selectSurveyAction(id: string, subjectType: SurveySubjectTypes) {
  return (
    dispatch: Dispatch<DataActionGetSurveydataRequest|DataActionGetSurveydataResponse|DataActionGetSurveydataError|SelectSurveyAction>,
    getState: () => { navigation: AnalysisSpiderParams } | undefined,
  ): void => {
    const state = getState();
    if (!state) {
      return;
    }
    dispatch({ type: DataActionTypes.get_surveydata_request });
    Promise.all([
      defaultPulseCheckApi.getSurvey(id),
      defaultPulseCheckApi.getSurveyTopics(id, { include: 'questions' }),
      defaultReportingApi.getSurveysCycles({ surveyIds: [id] }),
      defaultPulseCheckApi.getSurveySchedules(id),
    ])
      .then(res =>
        dispatch({
          type: DataActionTypes.get_surveydata_response,
          data: {
            survey: res[0].data,
            topics: res[1].data,
            cycles: res[2].data,
            schedules: res[3].data,
          },
        }),
      )
      .catch(() => dispatch({ type: DataActionTypes.get_surveydata_error }));

    dispatch({
      type: NavigationActions.selectSurvey,
      id,
      subjectType,
    });
  };
}

export function selectAboutSegmentsAction(segmentIds: string[]): SelectAboutSegmentAction {
  return {
    type: NavigationActions.selectAboutSegments,
    ids: segmentIds,
  };
}

export function selectCyclesAction(cycleIds: string[]): SelectCyclesAction {
  return {
    type: NavigationActions.selectCycles,
    ids: cycleIds,
  };
}

export function selectTopicAction(topicId: string): SelectTopicAction {
  return {
    type: NavigationActions.selectTopic,
    id: topicId,
  };
}

export function setIncludeSelfAssessmentAction(isIncluded: boolean): SetIncludeSelfAssessmentAction {
  return {
    type: NavigationActions.setIncludeSelfAssessment,
    isIncluded,
  };
}

export function selectAskSegmentsAction(segmentIds: string[]): SelectAskSegmentAction {
  return {
    type: NavigationActions.selectAskSegments,
    ids: segmentIds,
  };
}

export function deselectTopicAction(): DeselectTopicAction {
  return {
    type: NavigationActions.deselectTopic,
  };
}

export function setActiveStepIndex(index: number): SetActiveStepIndexAction {
  return {
    type: NavigationActions.setActiveStepIndex,
    index,
  };
}
